import React, { useEffect, useState } from "react";
import { feesApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { TID } from "@/lib/testIds";
import { Receipt, DollarSign, TrendingUp, FileDown, Zap } from "lucide-react";
import { toast } from "sonner";

export default function FeeStatus({ onReminder }) {
  const [summary, setSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [autopayBusy, setAutopayBusy] = useState(false);

  const load = async () => {
    try {
      const s = await feesApi.summary();
      setSummary(s);
      onReminder?.(s);
    } catch (err) { console.debug("[FeeStatus] summary poll failed", err); }
  };
  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const settle = async () => {
    try {
      setBusy(true);
      const { checkout_url } = await feesApi.checkout();
      window.location.href = checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe checkout");
    } finally { setBusy(false); }
  };

  const toggleAutopay = async (enabled) => {
    try {
      setAutopayBusy(true);
      await feesApi.setAutopay(enabled);
      toast.success(enabled ? "Auto-pay enabled" : "Auto-pay disabled");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update auto-pay");
    } finally { setAutopayBusy(false); }
  };

  const downloadStatement = () => {
    window.open(feesApi.statementUrl(), "_blank", "noopener,noreferrer");
  };

  if (!summary) return null;
  const owed = summary.amount_owed;
  const hasFee = owed > 0.005;
  const threshold = summary.reminder_threshold || 10;
  const overThreshold = owed >= threshold;

  return (
    <div data-testid={TID.feeStatusCard}
      className={`aq-card p-4 ${overThreshold ? "border-amber-500/40 bg-amber-500/5" : hasFee ? "border-emerald-500/30 bg-emerald-500/5" : ""}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-emerald-400" />
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Platform fee (1.25%)</div>
        </div>
        <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300">
          Free to trade
        </span>
      </div>

      <div className="mt-3 rounded-md bg-black/40 border border-white/5 p-3 space-y-1">
        <Row k="Fees owed" v={`$${owed.toFixed(2)}`} highlight={hasFee} />
        <Row k="Profitable trades" v={summary.profitable_trades} />
        <Row k="Lifetime profit" v={`$${summary.realized_profit_lifetime.toFixed(2)}`} />
        <Row k="Lifetime fees paid" v={`$${summary.amount_paid_lifetime.toFixed(2)}`} />
      </div>

      {hasFee ? (
        <Button
          data-testid={TID.feePayBtn}
          onClick={settle}
          disabled={busy || owed < 0.5}
          className="w-full mt-3 h-10 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black font-semibold"
        >
          <DollarSign className="w-3.5 h-3.5 mr-1.5" />
          {busy ? "Redirecting…" : owed < 0.5 ? `Pay ($${owed.toFixed(2)} · min $0.50)` : `Pay $${owed.toFixed(2)} via Stripe`}
        </Button>
      ) : (
        <p className="mt-3 text-xs text-slate-400 leading-relaxed">
          No fee to pay right now. 1.25% is only charged on <b>profitable</b> closed trades — you keep 100% of losses and break-evens.
        </p>
      )}

      {/* Auto-pay */}
      <div className="mt-3 rounded-md border border-white/10 bg-white/5 p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs">
            <Zap className={`w-3.5 h-3.5 ${summary.autopay_enabled ? "text-emerald-400" : "text-slate-500"}`} />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Auto-pay when ≥ ${threshold}</span>
          </div>
          <Switch
            data-testid={TID.feeAutopayToggle}
            checked={!!summary.autopay_enabled}
            disabled={autopayBusy || !summary.card_on_file}
            onCheckedChange={toggleAutopay}
          />
        </div>
        {summary.card_on_file ? (
          <div className="mt-1 text-[11px] text-slate-500 font-mono">
            Card on file: {summary.card_brand || "card"} •••• {summary.card_last4 || "----"}
          </div>
        ) : (
          <div className="mt-1 text-[11px] text-slate-500 leading-snug">
            Pay a fee once to save your card, then auto-pay can settle future balances above the threshold automatically.
          </div>
        )}
      </div>

      <Button
        data-testid={TID.feeStatementBtn}
        onClick={downloadStatement}
        variant="ghost"
        className="w-full mt-2 h-9 text-slate-300 hover:text-white hover:bg-white/10"
      >
        <FileDown className="w-3.5 h-3.5 mr-1.5" /> Download monthly statement (PDF)
      </Button>

      <div className="mt-3 flex items-center gap-1 text-[10px] font-mono text-slate-500">
        <TrendingUp className="w-3 h-3" /> Broker holds your funds · Fees settled separately via Stripe
      </div>
    </div>
  );
}

function Row({ k, v, highlight }) {
  return (
    <div className="flex justify-between text-xs">
      <span className="text-slate-500 font-mono">{k}</span>
      <span className={`font-mono font-semibold ${highlight ? "text-emerald-300" : "text-slate-200"}`}>{v}</span>
    </div>
  );
}
